import React, { useState, useMemo } from 'react';
import { useApp } from '../../contexts/AppContext';
import { calculateSingleProperty, formatValue } from '../../utils';
import { 
  Trash2, 
  Edit, 
  Star, 
  ChevronUp, 
  ChevronDown,
  Download,
  Upload
} from 'lucide-react';

interface PortfolioTableProps {}

type SortColumn = 'name' | 'angebotspreis' | 'wohnflaeche' | 'cashflow' | 'roi' | 'vermoegen' | 'eigentumsanteil';
type SortDirection = 'asc' | 'desc';

const PortfolioTable: React.FC<PortfolioTableProps> = () => {
  const { 
    portfolio, 
    deleteProperty, 
    loadProperty,
    toggleFavorite,
    exportPortfolioToCSV,
    importPortfolioFromCSV
  } = useApp();

  const [selectedProperties, setSelectedProperties] = useState<Set<string>>(new Set());
  const [sortColumn, setSortColumn] = useState<SortColumn>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [showComparison, setShowComparison] = useState(false);

  // Calculate results for each property
  const portfolioWithCalculations = useMemo(() => {
    return portfolio.map(property => ({
      ...property,
      calculations: calculateSingleProperty(property.inputs)
    }));
  }, [portfolio]);

  // Sort portfolio
  const sortedPortfolio = useMemo(() => {
    const sorted = [...portfolioWithCalculations].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      if (sortColumn === 'cashflow') {
        aValue = a.calculations.cashflowMonatlichNachSteuer;
        bValue = b.calculations.cashflowMonatlichNachSteuer;
      } else if (sortColumn === 'roi') {
        aValue = a.calculations.eigenkapitalrendite;
        bValue = b.calculations.eigenkapitalrendite;
      } else if (sortColumn === 'vermoegen') {
        aValue = a.calculations.projectionData[10]?.vermoegen || 0;
        bValue = b.calculations.projectionData[10]?.vermoegen || 0;
      } else if (sortColumn === 'eigentumsanteil') {
        aValue = a.calculations.projectionData[10]?.eigentumsanteil || 0;
        bValue = b.calculations.projectionData[10]?.eigentumsanteil || 0;
      } else if (sortColumn === 'name') {
        aValue = a.name;
        bValue = b.name;
      } else if (sortColumn === 'angebotspreis') {
        aValue = a.inputs.angebotspreis;
        bValue = b.inputs.angebotspreis;
      } else if (sortColumn === 'wohnflaeche') {
        aValue = a.inputs.wohnflaeche;
        bValue = b.inputs.wohnflaeche;
      }

      if (typeof aValue === 'string') {
        return sortDirection === 'asc' 
          ? aValue.localeCompare(bValue) 
          : bValue.localeCompare(aValue);
      }

      return sortDirection === 'asc' 
        ? aValue - bValue 
        : bValue - aValue;
    });

    return sorted;
  }, [portfolioWithCalculations, sortColumn, sortDirection]);

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedProperties(new Set(portfolio.map(p => p.id)));
    } else {
      setSelectedProperties(new Set());
    }
  };

  const handleSelectProperty = (id: string) => {
    const newSelected = new Set(selectedProperties);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedProperties(newSelected);
  };

  const handleDeleteSelected = () => {
    if (window.confirm(`Möchten Sie ${selectedProperties.size} Immobilien löschen?`)) {
      selectedProperties.forEach(id => deleteProperty(id));
      setSelectedProperties(new Set());
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      importPortfolioFromCSV(file);
    }
  };

  const SortIcon = ({ column }: { column: SortColumn }) => {
    if (sortColumn !== column) return null;
    return sortDirection === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />;
  };

  // Calculate portfolio summary
  const portfolioSummary = useMemo(() => {
    const totalInvestment = sortedPortfolio.reduce((sum, p) => sum + p.calculations.gesamtkosten, 0);
    const totalEquity = sortedPortfolio.reduce((sum, p) => sum + p.calculations.eigenkapitalSoll, 0);
    const totalMonthlyCashflow = sortedPortfolio.reduce((sum, p) => sum + p.calculations.cashflowMonatlichNachSteuer, 0);
    const avgROI = sortedPortfolio.length > 0 
      ? sortedPortfolio.reduce((sum, p) => sum + p.calculations.eigenkapitalrendite, 0) / sortedPortfolio.length
      : 0;

    return {
      totalInvestment,
      totalEquity,
      totalMonthlyCashflow,
      avgROI
    };
  }, [sortedPortfolio]);

  if (portfolio.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 bg-gray-50 rounded-lg">
        <p className="text-gray-500 mb-4">Keine Immobilien im Portfolio</p>
        <p className="text-sm text-gray-400">Speichern Sie Immobilien aus der Analyse, um sie hier zu sehen</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Control Bar */}
      <div className="flex flex-wrap items-center justify-between gap-4 p-4 bg-white rounded-lg shadow">
        <div className="flex gap-2">
          <button
            onClick={exportPortfolioToCSV}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
          >
            <Download size={16} />
            Export CSV
          </button>
          <label className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 cursor-pointer">
            <Upload size={16} />
            Import CSV
            <input
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              className="hidden"
            />
          </label>
          {selectedProperties.size > 0 && (
            <button
              onClick={handleDeleteSelected}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
            >
              <Trash2 size={16} />
              Löschen ({selectedProperties.size})
            </button>
          )}
        </div>
        <button
          onClick={() => setShowComparison(!showComparison)}
          className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
        >
          {showComparison ? 'Tabelle anzeigen' : 'Vergleich anzeigen'}
        </button>
      </div>

      {/* Portfolio Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-600">Gesamtinvestition</div>
          <div className="text-xl font-bold">{formatValue(portfolioSummary.totalInvestment, '€')}</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-600">Eigenkapital gesamt</div>
          <div className="text-xl font-bold">{formatValue(portfolioSummary.totalEquity, '€')}</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-600">Monatl. Cashflow gesamt</div>
          <div className="text-xl font-bold">{formatValue(portfolioSummary.totalMonthlyCashflow, '€')}</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-600">Durchschn. ROI</div>
          <div className="text-xl font-bold">{formatValue(portfolioSummary.avgROI, '%')}</div>
        </div>
      </div>

      {/* Table */}
      {!showComparison ? (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedProperties.size === portfolio.length}
                      onChange={handleSelectAll}
                      className="rounded"
                    />
                  </th>
                  <th className="px-3 py-3 text-left">
                    <Star size={16} className="text-gray-400" />
                  </th>
                  <th 
                    className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center gap-1">
                      Name
                      <SortIcon column="name" />
                    </div>
                  </th>
                  <th 
                    className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort('angebotspreis')}
                  >
                    <div className="flex items-center gap-1">
                      Preis
                      <SortIcon column="angebotspreis" />
                    </div>
                  </th>
                  <th 
                    className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort('wohnflaeche')}
                  >
                    <div className="flex items-center gap-1">
                      Fläche
                      <SortIcon column="wohnflaeche" />
                    </div>
                  </th>
                  <th 
                    className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort('cashflow')}
                  >
                    <div className="flex items-center gap-1">
                      Monatl. CF
                      <SortIcon column="cashflow" />
                    </div>
                  </th>
                  <th 
                    className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort('roi')}
                  >
                    <div className="flex items-center gap-1">
                      ROI
                      <SortIcon column="roi" />
                    </div>
                  </th>
                  <th 
                    className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort('vermoegen')}
                  >
                    <div className="flex items-center gap-1">
                      Vermögen (10J)
                      <SortIcon column="vermoegen" />
                    </div>
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Aktionen
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedPortfolio.map((property) => {
                  const isSelected = selectedProperties.has(property.id);
                  const cashflowClass = property.calculations.cashflowMonatlichNachSteuer < 0 
                    ? 'text-red-600' 
                    : 'text-green-600';
                  
                  return (
                    <tr key={property.id} className={isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'}>
                      <td className="px-3 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleSelectProperty(property.id)}
                          className="rounded"
                        />
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap">
                        <button
                          onClick={() => toggleFavorite(property.id)}
                          className={`${property.isFavorite ? 'text-yellow-500' : 'text-gray-300'} hover:text-yellow-500`}
                        >
                          <Star size={16} fill={property.isFavorite ? 'currentColor' : 'none'} />
                        </button>
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {property.name}
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatValue(property.inputs.angebotspreis, '€')}
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                        {property.inputs.wohnflaeche} m²
                      </td>
                      <td className={`px-3 py-4 whitespace-nowrap text-sm font-semibold ${cashflowClass}`}>
                        {formatValue(property.calculations.cashflowMonatlichNachSteuer, '€')}
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatValue(property.calculations.eigenkapitalrendite, '%')}
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatValue(property.calculations.projectionData[10]?.vermoegen || 0, '€')}
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex gap-2">
                          <button
                            onClick={() => loadProperty(property.id)}
                            className="text-blue-600 hover:text-blue-900"
                            title="Bearbeiten"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={() => {
                              if (window.confirm(`"${property.name}" löschen?`)) {
                                deleteProperty(property.id);
                              }
                            }}
                            className="text-red-600 hover:text-red-900"
                            title="Löschen"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <PortfolioComparison 
          properties={sortedPortfolio.filter(p => selectedProperties.has(p.id))}
        />
      )}
    </div>
  );
};

// Comparison Component
const PortfolioComparison: React.FC<{ properties: any[] }> = ({ properties }) => {
  if (properties.length === 0) {
    return (
      <div className="bg-white p-8 rounded-lg shadow text-center">
        <p className="text-gray-500">Wählen Sie Immobilien aus der Tabelle aus, um sie zu vergleichen</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead>
            <tr className="bg-gray-50">
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Eigenschaft</th>
              {properties.map(p => (
                <th key={p.id} className="px-4 py-2 text-left text-xs font-medium text-gray-500">
                  {p.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            <tr>
              <td className="px-4 py-2 font-medium">Angebotspreis</td>
              {properties.map(p => (
                <td key={p.id} className="px-4 py-2">{formatValue(p.angebotspreis, '€')}</td>
              ))}
            </tr>
            <tr>
              <td className="px-4 py-2 font-medium">Wohnfläche</td>
              {properties.map(p => (
                <td key={p.id} className="px-4 py-2">{p.wohnflaeche} m²</td>
              ))}
            </tr>
            <tr>
              <td className="px-4 py-2 font-medium">Kaltmiete/m²</td>
              {properties.map(p => (
                <td key={p.id} className="px-4 py-2">{formatValue(p.kaltmieteProQm, '€')}</td>
              ))}
            </tr>
            <tr>
              <td className="px-4 py-2 font-medium">Eigenkapital</td>
              {properties.map(p => (
                <td key={p.id} className="px-4 py-2">{formatValue(p.calculations.eigenkapitalSoll, '€')}</td>
              ))}
            </tr>
            <tr>
              <td className="px-4 py-2 font-medium">Monatl. Cashflow</td>
              {properties.map(p => (
                <td key={p.id} className={`px-4 py-2 font-semibold ${p.calculations.cashflowNachSteuerMonatlich < 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {formatValue(p.calculations.cashflowNachSteuerMonatlich, '€')}
                </td>
              ))}
            </tr>
            <tr>
              <td className="px-4 py-2 font-medium">ROI</td>
              {properties.map(p => (
                <td key={p.id} className="px-4 py-2">{formatValue(p.calculations.eigenkapitalrenditeProzent, '%')}</td>
              ))}
            </tr>
            <tr>
              <td className="px-4 py-2 font-medium">Vermögen nach 10J</td>
              {properties.map(p => (
                <td key={p.id} className="px-4 py-2">{formatValue(p.calculations.projectionData[10]?.vermoegen || 0, '€')}</td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PortfolioTable;